// graphNodeSearch — find a node anywhere in the three graphs, by any word on it.
//
// Backs the Ctrl+F box in the graph editors. Pure over injected graph lists so
// `node --test` can drive it; the caller passes the LIVE module refs
// (graphState / faceGraphState / eventGraphState), which is deliberate — a
// search that read the server's saved copy would not find the node you added
// thirty seconds ago.
//
// Containers are uniform across all three graphs: a node holding a sub-graph
// stores it inline as `data.innerNodes`. That is true of the event graph's
// `graphGroup` AND the body/face graphs' `stateMachine`, so one walker covers
// both and there is deliberately no per-type branch. Event FUNCTIONS are the
// one other pool (`functions[id] = { name, nodes }`).
//
// One level deep, matching what the editors can navigate to. Finding something
// the UI cannot open would recreate the very problem this replaces: an answer
// that names a place you can't get to.
//
// This is the client twin of aos-mcp's src/graph/nodeSearch.js (same match
// shape, same ranking) — the agent searches the SAVED blueprint server-side,
// Ctrl+F searches the LIVE one here. Keep the two in step when either changes.

/**
 * The name a person would use for a node. Node data has no single naming field.
 *
 * Order is deliberate: an explicit name/label the author typed beats anything
 * derived. `varName` is what setVariable/getVariable and the whole array/map node
 * family actually store (`variableName` appears nowhere in defaultNodeData), and
 * `value` is what the literal nodes store — without those two, a graph's most
 * numerous nodes all label themselves by their bare type and become
 * indistinguishable in any list, which is the round-2 "jumps to the first one"
 * report.
 *
 * `value` is read last and numerically-aware: a literal `0` or `8` is a perfectly
 * good name, but `''` is not, so an empty literal falls through to its type
 * rather than rendering a blank row.
 */
export function nodeLabel(node) {
  const d = node?.data ?? {}
  const first = [
    d.name, d.label, d.title, d.clipName, d.functionName, d.variableName, d.varName,
    d.text, d.key, d.button, d.axis, d.environmentName,
  ].find((v) => typeof v === 'string' && v.trim())
  if (first) return first.trim()
  if (typeof d.value === 'number' && Number.isFinite(d.value)) return String(d.value)
  if (typeof d.value === 'string' && d.value.trim()) return d.value.trim()
  return String(node?.type ?? 'node')
}

function searchableStrings(node) {
  const out = [String(node?.id ?? ''), String(node?.type ?? ''), nodeLabel(node)]
  for (const v of Object.values(node?.data ?? {})) {
    if (typeof v === 'string' && v.length <= 200) out.push(v)
  }
  return out
}

// Ranked so an exact id beats an exact type beats a word buried in a URL. The
// ranking is what stops a vague query being answered with a confident jump to
// the wrong node — it orders the list the user then picks from.
const SCORE = { id: 100, type: 70, label: 55, contains: 30 }

// Space-insensitive, the same way the add-node palette has always been (Unreal
// style: "getcharloc" finds Get Character Location). Without it "play audio"
// matched the palette's CATALOG entry and none of the playAudio nodes already in
// the graph, which is what made search look like it could only ever add a new
// one.
const squash = (v) => String(v ?? '').toLowerCase().replace(/\s+/g, '')

function scoreNode(node, q) {
  const id = squash(node?.id)
  const type = squash(node?.type)
  const label = squash(nodeLabel(node))
  if (id === q) return SCORE.id
  if (type === q) return SCORE.type
  if (label === q) return SCORE.label
  if (id.includes(q) || label.includes(q)) return SCORE.label - 5
  if (type.includes(q)) return SCORE.type - 25
  if (searchableStrings(node).some((s) => squash(s).includes(q))) return SCORE.contains
  return 0
}

function match(node, graph, container, q, out) {
  const score = scoreNode(node, q)
  if (!score) return
  out.push({
    graph,
    nodeId: node.id,
    type: node.type,
    label: nodeLabel(node),
    position: node.position ?? null,
    container,
    score,
  })
}

const GRAPH_ORDER = ['body', 'face', 'event']

/**
 * Every node the editors can navigate to, with where it lives. ONE traversal,
 * shared by Ctrl+F (`findGraphNodes`), the MY BLUEPRINT rail and the add-node
 * palette — so the three can never disagree about whether a node exists or which
 * container holds it. Order is stable (graph order, then authoring order), which
 * is what lets a grouped list read the same way twice.
 *
 * @param {object} graphs  { body:{nodes}, face:{nodes}, event:{nodes, functions} }
 * @param {object} [opts]
 * @param {string} [opts.graph]  restrict to one graph; omit for all three
 * @returns {Array<{graph: string, node: object, container: object|null}>}
 */
export function collectGraphNodes(graphs, { graph = null } = {}) {
  const out = []
  const wanted = graph ? [graph] : GRAPH_ORDER
  for (const g of wanted) {
    for (const node of graphs?.[g]?.nodes ?? []) {
      out.push({ graph: g, node, container: null })
      const inner = node?.data?.innerNodes
      if (!Array.isArray(inner)) continue
      const container = { kind: 'group', id: node.id, type: node.type, name: nodeLabel(node) }
      for (const child of inner) out.push({ graph: g, node: child, container })
    }
  }
  if (!graph || graph === 'event') {
    for (const [id, fn] of Object.entries(graphs?.event?.functions ?? {})) {
      const container = { kind: 'function', id, type: 'function', name: fn?.name ?? id }
      for (const child of fn?.nodes ?? []) out.push({ graph: 'event', node: child, container })
    }
  }
  return out
}

/**
 * Where two rows would read the same, name the id.
 *
 * The point of a list of nodes is that you can choose between them, and no list
 * of data fields can promise that: the shipped Orbit Camera Rig holds three
 * onPointerDrag nodes, the conversational blueprint has eight setVariable nodes
 * all driving ConversationalState, and a node type added tomorrow may carry
 * nothing identifying at all. This is the guarantee that survives all three.
 *
 * Rows whose labels already differ are untouched, so the common case stays
 * clean. Shared by the MY BLUEPRINT rail and the palette's in-graph matches, so
 * the two cannot disagree about when a row needs qualifying.
 *
 * @param {Array<{nodeId: string, label: string}>} rows
 */
export function disambiguateLabels(rows) {
  const seen = new Map()
  for (const n of rows ?? []) seen.set(n.label, (seen.get(n.label) ?? 0) + 1)
  return (rows ?? []).map((n) => (seen.get(n.label) > 1 ? { ...n, label: `${n.label} (${n.nodeId})` } : n))
}

/**
 * The same walk, grouped by node type — what the MY BLUEPRINT rail renders.
 *
 * The rail used to filter the top-level `nodes` array directly, which lost every
 * node inside a graphGroup or a function and gave two instances of one type the
 * identical row. Grouping here means the rail states a COUNT and can expand to
 * per-instance rows that `nodeLabel` tells apart.
 *
 * @param {object} graphs
 * @param {object} [opts]
 * @param {string} [opts.graph]
 * @param {Set<string>} [opts.types]  restrict to these node types
 * @returns {Array<{type: string, count: number, nodes: Array<{nodeId,type,label,graph,container}>}>}
 */
export function listNodesByType(graphs, { graph = null, types = null } = {}) {
  const byType = new Map()
  for (const { graph: g, node, container } of collectGraphNodes(graphs, { graph })) {
    const type = String(node?.type ?? '')
    if (!type) continue
    if (types && !types.has(type)) continue
    if (!byType.has(type)) byType.set(type, [])
    byType.get(type).push({ nodeId: node.id, type, label: nodeLabel(node), graph: g, container: container ?? null })
  }
  return [...byType.entries()].map(([type, nodes]) => ({ type, count: nodes.length, nodes: disambiguateLabels(nodes) }))
}

/**
 * @param {object} graphs  { body:{nodes}, face:{nodes}, event:{nodes, functions} }
 * @param {string} query
 * @param {object} [opts]
 * @param {string} [opts.preferGraph]  the open graph — its matches sort first
 * @param {number} [opts.limit]
 * @returns {Array<{graph,nodeId,type,label,position,container,score}>}
 */
export function findGraphNodes(graphs, query, { preferGraph = null, limit = 40 } = {}) {
  const q = squash(query)
  if (!q) return []
  const out = []
  for (const { graph, node, container } of collectGraphNodes(graphs)) {
    match(node, graph, container, q, out)
  }

  // The graph you are looking at wins ties: Ctrl+F is "find here" first,
  // "find anywhere" second (Jakob's Law — that is what Ctrl+F means everywhere
  // else), while still surfacing the other graphs rather than hiding them.
  const bias = (m) => (preferGraph && m.graph === preferGraph ? 1000 : 0)
  return out
    .sort((a, b) => (b.score + bias(b)) - (a.score + bias(a)) || String(a.nodeId).localeCompare(String(b.nodeId)))
    .slice(0, limit)
}

// "3700,2600" · "(3700, 2600)" · "3700 2600" · "x: 3700 y: -260" · "3700.5,-2600"
// Anything else — including a single number, which is a search term and not a
// place — returns null so the box treats it as text.
const COORD = /^\(?\s*(?:x\s*[:=]\s*)?(-?\d+(?:\.\d+)?)\s*(?:,|;|\s)\s*(?:y\s*[:=]\s*)?(-?\d+(?:\.\d+)?)\s*\)?$/i

/** Parse a pasted coordinate. Returns {x,y} or null. */
export function parseCoordinate(text) {
  const m = COORD.exec(String(text ?? '').trim())
  if (!m) return null
  const x = Number(m[1])
  const y = Number(m[2])
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null
}

/** Where a match lives, in words. Never coordinates — those are what failed. */
export function describeLocation(m, graphLabels = { body: 'Body graph', face: 'Face graph', event: 'Event graph' }) {
  const g = graphLabels[m.graph] ?? m.graph
  if (!m.container) return g
  return m.container.kind === 'function'
    ? `${g} › function "${m.container.name}"`
    : `${g} › ${m.container.name}`
}
